import React from "react";
import { ExternalLink, Check, Package, Layers, Scissors, Flag } from "lucide-react";
import { useQuote } from "../QuoteDialog";

const POINTS = [
  { icon: Package, text: "Printer + supplies + finishing" },
  { icon: Layers, text: "Hot or cold laminator included" },
  { icon: Scissors, text: "Vinyl cutter for decals" },
  { icon: Flag, text: "Optional banner finishing kit" },
];

export default function BundlesHero() {
  const { openQuote } = useQuote();

  return (
    <section
      data-testid="bundles-hero-section"
      className="relative isolate overflow-hidden bg-navy"
    >
      <div className="absolute inset-0">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
        <div className="absolute -top-40 -right-40 w-[28rem] h-[28rem] bg-teal/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-[24rem] h-[24rem] bg-teal/10 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-20 pb-24 md:pt-28 md:pb-32 lg:pt-36 lg:pb-40">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white text-xs font-bold uppercase tracking-[0.18em] mb-8 animate-fade-up">
            <span className="w-1.5 h-1.5 rounded-full bg-teal" />
            Complete print systems for K-12 schools
          </div>

          <h1
            data-testid="bundles-hero-headline"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tight text-white leading-[1.05] animate-fade-up"
            style={{ animationDelay: "80ms" }}
          >
            Everything Your School Needs to{" "}
            <span className="text-teal">Print In-House.</span>
          </h1>

          <p
            data-testid="bundles-hero-subheadline"
            className="mt-8 text-lg sm:text-xl text-slate-200 leading-relaxed max-w-2xl animate-fade-up"
            style={{ animationDelay: "160ms" }}
          >
            Printer, supplies, and finishing tools—bundled into one complete
            system.
          </p>

          <div className="mt-10 animate-fade-up" style={{ animationDelay: "240ms" }}>
            <button
              data-testid="bundles-hero-quote-btn"
              onClick={openQuote}
            title="Opens in a new tab"
              className="group inline-flex items-center justify-center gap-2 bg-teal hover:bg-[#0D6BB1] text-white font-bold px-8 py-4 rounded-lg shadow-xl hover:shadow-2xl transition-all duration-200 hover:-translate-y-0.5"
            >
              Request a Quote
              <ExternalLink className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>

          <ul
            className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 max-w-2xl animate-fade-up"
            style={{ animationDelay: "320ms" }}
          >
            {POINTS.map((p) => {
              const Icon = p.icon;
              return (
                <li key={p.text} className="flex items-start gap-3 text-slate-200">
                  <span className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-full bg-teal/20 border border-teal/40 flex items-center justify-center">
                    <Check className="w-3.5 h-3.5 text-teal" strokeWidth={2.5} />
                  </span>
                  <span className="text-sm font-medium flex items-center gap-2">
                    <Icon className="w-4 h-4 text-teal" strokeWidth={2.5} />
                    {p.text}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}
